import { getMinPrice, getTotalPrice as getTotalPriceBase } from '@shared/lib/money';
import {
	CombinedDirection,
	CombinedWith,
	GdsSeatService,
	ReplaceNestedType,
	RowPart,
	RowWithParts,
	Seat,
	SeatMapSegmentWithRowParts,
	SeatMapWithRowParts,
	SeatMapWithSeatsWithService,
	Segment,
	ServicesMap,
	PassengerFiltersData,
	BuildPassengerSegmentsOptions
} from './types';
import { GdsServiceById, isSeatChangeChargeGdsService } from '@shared/lib/gdsService';
import { EMPTY_PASSENGER_FILTERS_DATA } from './constants';
import { MoneyType } from '../PriceMatrix/types';
import { Money } from '../types';
import { PassengerState } from '../Checkout/store/passengers/reducers';
import memes from '../memes';
import { RunServiceRequestPassengers } from '../Checkout/types';
import { SelectedSeatsServiceType } from '../Checkout/store/selectedServices/reducers';
import { isOccupiedByOtherPassenger } from './components/SeatMap/Seat/utils';
import {
	AviaPassengerType,
	FlightSeatMapFragment,
	FlightSeatMapRowFragment,
	FlightSeatMapSegmentFragment,
	FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment,
	FlightStaticInfoFragment,
	OrderAdditionalServiceGdsServiceServiceType,
	OrderAdditionalServicesFragment,
	RowSeatFragment,
	SeatMapRestrictions
} from '@websky/graphql';

export const getTabs = (segments: Segment[], passengers: PassengerState[], servicesMap: ServicesMap) => {
	return segments.map((segment, index) => {
		let seatsCount = 0;
		const prices: MoneyType[] = [];

		passengers.forEach(passenger => {
			if (!passenger.seats) {
				return;
			}
			const seats = passenger.seats.filter(seat => seat.segmentId === segment.segmentInfo.id);

			seats.forEach(seat => {
				seatsCount++;

				if (seat?.rfisc) {
					const servicePrice = servicesMap[seat.segmentId]?.[passenger.id]?.[seat.id]?.price;

					prices.push({
						amount: servicePrice?.amount ?? 0,
						currency: seat?.price?.currency
					});
				} else {
					prices.push({
						amount: seat?.price?.amount ?? 0,
						currency: seat?.price?.currency
					});
				}
			});
		});

		return {
			value: index,
			isAvailable: segment.decks.length > 0,
			header: `${segment.segmentInfo.departure.airport.city.name} — ${segment.segmentInfo.arrival.airport.city.name}`,
			seatsCount: seatsCount,
			price: seatsCount > 0 ? getTotalPriceBase(prices, price => price) : null
		};
	});
};

export const getSeatServicesForSegments = (segment: SeatMapSegmentWithRowParts): GdsSeatService[] => {
	const uniqueServiceIdsSet = new Set<string>();
	const uniqueServices: FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[] = [];

	if (segment && segment.decks.length) {
		segment.decks[0].rows.forEach(row => {
			row.parts.forEach(part => {
				part.seats.forEach(seat => {
					if (seat.seatService && !uniqueServiceIdsSet.has(seat.seatService.id)) {
						const seatService = seat.service;

						if (seatService) {
							uniqueServices.push(seatService);
							uniqueServiceIdsSet.add(seatService.id);
						}
					}
				});
			});
		});

		return uniqueServices;
	}

	return [];
};

const getSeatsOnOtherSegments = (
	traveller: PassengerState,
	segment: FlightSeatMapSegmentFragment
): SelectedSeatsServiceType[] => {
	return traveller.seats?.filter(travellerSeat => travellerSeat.segmentId !== segment.segmentsInfo.id) || [];
};

export const updateSeatServices = (
	passengers: PassengerState[],
	segment: FlightSeatMapSegmentFragment,
	passengerId: string,
	seats: Seat[]
): RunServiceRequestPassengers[] => {
	const services: RunServiceRequestPassengers[] = [];
	const currentPassenger = passengers.find(passenger => passenger.id === passengerId);

	const otherSelectedSeats = getSeatsOnOtherSegments(currentPassenger, segment).map(seat => ({
		isConfirmed: seat.isConfirmed,
		segment: {
			id: seat.segmentId
		},
		letter: seat.letter,
		row: parseInt(seat.number, 10),
		seat
	}));

	seats.forEach(seat => {
		const isSameSeat =
			currentPassenger?.seats?.find(selectedSeat => selectedSeat.segmentId === segment.segmentsInfo.id)
				?.number === seat.number;

		const selectedSeat: RunServiceRequestPassengers['seats'][0] =
			isSameSeat || !seat
				? null
				: {
						isConfirmed: false,
						onRequest: false,
						isChangeable: true,
						segment: { id: segment.segmentsInfo.id },
						letter: seat.letter,
						row: parseInt(seat.number, 10),
						seat,
						product: null,
						type: null
				  };

		if (selectedSeat) {
			const isSeatOccupiedByOtherPassenger = isOccupiedByOtherPassenger(
				seat,
				passengers,
				currentPassenger.id,
				segment.segmentsInfo.id
			);

			if (isSeatOccupiedByOtherPassenger) {
				const passengerWithThisSeat = passengers.find(passenger =>
					passenger.seats?.some(
						passengerSeat =>
							`${passengerSeat.row}${passengerSeat.letter}` === seat.number &&
							passengerSeat.segmentId === segment.segmentsInfo.id
					)
				);

				const otherPassengerSeats = getSeatsOnOtherSegments(passengerWithThisSeat, segment);

				services.push({
					passengerId: passengerWithThisSeat.id,
					seats: [
						...otherPassengerSeats.map(seat => ({
							isConfirmed: seat.isConfirmed,
							segment: {
								id: seat.segmentId
							},
							letter: seat.letter,
							row: parseInt(seat.number, 10),
							seat
						}))
					] as any
				});
			}
		}

		services.push({
			passengerId: currentPassenger.id,
			seats: selectedSeat ? ([...otherSelectedSeats, selectedSeat] as any) : [...otherSelectedSeats]
		});
	});

	return services;
};

export const getSelectedServices = (
	traveller: PassengerState,
	segment: FlightSeatMapSegmentFragment,
	passenger: number,
	seat: RowSeatFragment
): SelectedSeatsServiceType[] => {
	const seats = getSeatsOnOtherSegments(traveller, segment);
	const confirmedSeats =
		traveller?.seats?.filter(seat => seat.segmentId === segment.segmentsInfo.id && seat?.isConfirmed) ?? [];

	if (seat) {
		seats.push({
			segmentId: segment.segmentsInfo.id,
			row: seat.number,
			letter: seat.letter,
			price: seat.price,
			id: seat.seatService.id,
			type: OrderAdditionalServiceGdsServiceServiceType.Seat,
			rfisc: seat.rfisc,
			deckId: null,
			restrictions: seat.restrictions
			// !fixme
			// isConfirmed: false
		});
	}

	return [...seats, ...confirmedSeats];
};

export const seatMapHasAnySeats = (seatMap: FlightSeatMapFragment): boolean => {
	return seatMap && seatMap.segments.some(segment => segment.decks.length > 0);
};

export const filterSeatServicesForPassenger = (
	services: RowSeatFragment['seatServices'] | undefined,
	passengerId?: string
): RowSeatFragment['seatServices'] => {
	if (!services?.length) {
		return [];
	}

	const servicesCanBeAdded = services.filter(
		service =>
			service.canBeAdded &&
			(!passengerId || service.allowedPassengers?.some(group => group?.some(id => id === passengerId)))
	);

	return servicesCanBeAdded;
};

export const getSeatPrice = (seat: Seat | Partial<SelectedSeatsServiceType>, passengerId?: string): Money => {
	const services = filterSeatServicesForPassenger(seat.seatServices, passengerId);

	if (services.length) {
		return getTotalPriceBase(services, service => service.price);
	}

	return seat.price;
};

export const getSeatChangeChargeServicesForPassenger = (
	seat: Seat | Partial<SelectedSeatsServiceType>,
	gdsServiceById: GdsServiceById,
	passengerId?: string
): NonNullable<RowSeatFragment['seatServices']> => {
	const seatServices = filterSeatServicesForPassenger(seat.seatServices, passengerId);

	return seatServices.filter(service => {
		if (!service?.id) {
			return false;
		}

		const catalogService = gdsServiceById[service.id];

		return !!catalogService && isSeatChangeChargeGdsService(catalogService);
	});
};

export const getSeatChangePriceForPassenger = (
	seat: Seat | Partial<SelectedSeatsServiceType>,
	gdsServiceById: GdsServiceById,
	passengerId?: string
): Money | null => {
	if (!gdsServiceById || !Object.keys(gdsServiceById).length) {
		return null;
	}

	const changeServices = getSeatChangeChargeServicesForPassenger(seat, gdsServiceById, passengerId);

	if (!changeServices.length) {
		return null;
	}

	return getTotalPriceBase(changeServices, service => service.price);
};

export const getMinSeatChangeChargePrice = (
	services: OrderAdditionalServicesFragment['gdsServices']['services'],
	segmentId: string,
	passengerId?: string
): Money | null => {
	const changeServices =
		services?.filter(service => {
			if (!isSeatChangeChargeGdsService(service) || !service.canBeAdded) {
				return false;
			}

			const isAllowedForSegment = service.allowedSegments?.some(group => group?.includes(segmentId));

			if (!isAllowedForSegment) {
				return false;
			}

			if (!passengerId) {
				return true;
			}

			return service.allowedPassengers?.some(group => group?.some(id => id === passengerId));
		}) ?? [];

	if (!changeServices.length) {
		return null;
	}

	return getMinPrice(changeServices, service => service.price);
};

export const segmentHasAvailableSeats = memes(
	(segment: FlightSeatMapFragment['segments'][0], services: OrderAdditionalServicesFragment): boolean => {
		const segmentId = segment.segmentsInfo.id;

		const seatServices: FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[] = (
			services.gdsServices?.services ?? []
		).filter(
			service => service.type === OrderAdditionalServiceGdsServiceServiceType.Seat
		) as FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment[];

		let hasAvailableSeats = false;

		for (const [, deck] of Object.entries(segment.decks)) {
			for (const [, row] of Object.entries(deck.rows)) {
				for (const [, seat] of Object.entries(row.seats)) {
					if (hasAvailableSeats) {
						return true;
					}

					if (!seat.seatService) {
						// free seat
						hasAvailableSeats = seat.isAvailable && seat.isExistent && !seat.isAisle;
						continue;
					}

					const relatedService = seatServices.find(service => service.id === seat.seatService.id);
					hasAvailableSeats =
						seat.isAvailable &&
						seat.isExistent &&
						!seat.isAisle &&
						relatedService.allowedSegments.some(allowedSegment => allowedSegment.includes(segmentId));
				}
			}
		}

		return hasAvailableSeats;
	}
);

const getSeatServiceById = memes(
	(services: OrderAdditionalServicesFragment['gdsServices']['services'], id: string = null) => {
		if (!services?.length) {
			return null;
		}

		return services.find(
			service => service.id === id
		) as FlightSeatMapService_OrderAdditionalServiceGdsServiceSeat_Fragment;
	}
);

export const addServicesToSeats = memes(
	(
		seatMap: FlightSeatMapFragment,
		additionalServices: OrderAdditionalServicesFragment
	): SeatMapWithSeatsWithService => {
		return {
			additionalServices,
			segments: seatMap.segments.map(segment => ({
				...segment,
				decks: segment.decks.map(deck => ({
					...deck,
					rows: deck.rows.map(row => ({
						...row,
						seats: row.seats.map<Seat>(seat => ({
							...seat,
							service: getSeatServiceById(
								additionalServices.gdsServices?.services,
								seat.seatService && seat.seatService.id
							),
							services: seat.seatServices?.map(service =>
								getSeatServiceById(additionalServices.gdsServices?.services, service.id)
							)
						}))
					}))
				}))
			}))
		};
	}
);

export const isAisle = (seat: Seat) => {
	return !seat.isExistent && seat.isAisle;
};

export const splitRows = memes(
	(rows: ReplaceNestedType<FlightSeatMapRowFragment[], RowSeatFragment, Seat>): RowWithParts[] => {
		return rows.map((row, rowIndex) => {
			const parts: RowPart[] = [
				{
					seats: [],
					service: {
						minPrice: row.seats[0]?.price && getSeatPrice(row.seats[0]),
						seatService: row.seats[0]?.seatService
					}
				}
			];

			let currentParts = parts[parts.length - 1];

			row.seats.forEach((seat, seatIndex) => {
				const prev = seatIndex > 0 ? row.seats[seatIndex - 1] : null;

				if ((prev && isAisle(prev)) || isAisle(seat)) {
					const length = parts.push({
						seats: [seat],
						service: {
							isAisle: isAisle(seat),
							seatService: seat.seatService,
							minPrice: getSeatPrice(seat)
						}
					});

					currentParts = parts[length - 1];
				} else {
					currentParts.seats.push(seat);

					const seatPrice = getSeatPrice(seat);

					if (!currentParts.service.seatService && seat.seatService) {
						currentParts.service.seatService = seat.seatService;
						currentParts.service.minPrice = seatPrice;
					}

					if (
						currentParts.service.minPrice.amount > seatPrice.amount &&
						seat.isExistent &&
						seat.service?.comfort === currentParts.service?.seatService?.comfort
					) {
						currentParts.service.minPrice = seatPrice;
					}
				}
			});

			return { ...row, parts };
		});
	}
);

export const splitSeatMapRows = memes(
	(seatMap: SeatMapWithSeatsWithService): SeatMapWithRowParts => {
		return {
			additionalServices: seatMap.additionalServices,
			segments: seatMap.segments.map(segment => ({
				...segment,
				decks: segment.decks.map(deck => ({
					...deck,
					rows: splitRows(deck.rows)
				}))
			}))
		};
	}
);

export const getSeatCombinedWith = (seats?: Seat[]) => (seat: Seat, index: number) => {
	const combinedWith: CombinedWith = {};

	const prevSeat = seats?.[index - 1] as Seat;
	const nextSeat = seats?.[index + 1] as Seat;

	const isPrevSeatAvailable = prevSeat && isSeatAvailableAndExistent(prevSeat);
	const isNextSeatAvailable = nextSeat && isSeatAvailableAndExistent(nextSeat);

	if (seat.isAvailable && isPrevSeatAvailable) {
		combinedWith[prevSeat.number] = CombinedDirection.Right;
	}

	if (seat.isAvailable && isNextSeatAvailable) {
		combinedWith[nextSeat.number] = CombinedDirection.Left;
	}

	return {
		...seat,
		combinedWith
	};
};

export const doubleSeatsAdapter = (segments: Segment[]): Segment[] => {
	return segments.map(segment => ({
		...segment,
		decks: segment.decks.map(deck => ({
			...deck,
			rows: deck.rows.map(row => ({
				...row,
				seats: row.seats?.map(getSeatCombinedWith(row.seats)),
				parts: row.parts.map(part => ({
					...part,
					seats: part.seats?.map(getSeatCombinedWith(part.seats))
				}))
			}))
		}))
	}));
};

export const isOccupiedSeat = (seat: Seat, selectedSeats: Map<string, SelectedSeatsServiceType[]>): boolean => {
	const selectedSeatsByNumber = selectedSeats.get(seat.number);

	if (!selectedSeatsByNumber?.length) {
		return false;
	}

	return !!selectedSeatsByNumber.find(
		selectedSeat =>
			selectedSeat.rfisc === seat.rfisc && selectedSeat.number === seat.number && selectedSeat.row === seat.row
	);
};

const INFANT_RELATED_SEAT_RESTRICTIONS = [SeatMapRestrictions.Infant, SeatMapRestrictions.PassengersUnder18];

export const filterNotInfantSeatRestrictions = (restriction: SeatMapRestrictions) => {
	return !INFANT_RELATED_SEAT_RESTRICTIONS.includes(restriction);
};

export const getLinkedPassengerTypes = (
	passenger: PassengerState,
	passengers: PassengerState[]
): Set<AviaPassengerType> => {
	const linkedPassengersTypeSet = new Set<AviaPassengerType>();

	if (!passenger.linkedTravellerIds?.length) {
		return linkedPassengersTypeSet;
	}

	const passengersMap = new Map(passengers.map(p => [p.id, p]));

	for (const linkedPassengerId of passenger.linkedTravellerIds) {
		const linkedTraveller = passengersMap.get(linkedPassengerId);

		if (!linkedTraveller) {
			continue;
		}

		linkedPassengersTypeSet.add(linkedTraveller.type);
	}

	return linkedPassengersTypeSet;
};

export const getAvailabilitySeat = (
	passenger: PassengerState,
	passengers: PassengerState[],
	selectedSeats: Map<string, SelectedSeatsServiceType[]>,
	isNewOrder?: boolean,
	hasDoubleSeats?: boolean
) => (seat: Seat): Seat => {
	let isAvailable = seat.isAvailable && seat.isExistent;
	const linkedPassengerTypes = getLinkedPassengerTypes(passenger, passengers);

	if (isAvailable && seat.restrictions?.length && passenger.seatRestrictions?.length) {
		// We filter out restrictions related to infants for adult passengers
		// if an infant with a seat (INS) is booked with them, so as not to block
		// bookings due to those restrictions.
		const passengerSeatRestrictions = linkedPassengerTypes.has(AviaPassengerType.INS)
			? passenger.seatRestrictions.filter(filterNotInfantSeatRestrictions)
			: passenger.seatRestrictions;

		const hasMatchingSeatRestriction = seat.restrictions.some(restriction =>
			passengerSeatRestrictions.includes(restriction)
		);

		if (hasMatchingSeatRestriction) {
			isAvailable = false;
		}
	}

	if (isAvailable && seat.restrictions?.length) {
		// We'll deactivate the seat if the restrictions include infant (INF)
		// seating and an adult passenger has linked infants
		if (seat.restrictions.includes(SeatMapRestrictions.Infant) && linkedPassengerTypes.has(AviaPassengerType.INF)) {
			isAvailable = false;
		}
	}

	// in a booked order with selected double seats, we make all seats inaccessible, except for the selected ones
	if (isAvailable && !isNewOrder) {
		isAvailable = !hasDoubleSeats && !isOccupiedSeat(seat, selectedSeats);
	}

	// if the seat is unavailable, but still selected, check if it is a double seat, so that the passenger can remove it
	if (!isAvailable && !hasDoubleSeats && isOccupiedSeat(seat, selectedSeats)) {
		const isDoubleSeat = selectedSeats.get(seat.number)?.length > 1;

		isAvailable = !isDoubleSeat;
	}

	return {
		...seat,
		isAvailable
	};
};

export const getSegments = (
	seatMap: SeatMapWithRowParts,
	passenger: PassengerState,
	passengers: PassengerState[],
	selectedSeats: Map<string, SelectedSeatsServiceType[]>,
	amenities: FlightStaticInfoFragment['amenities'],
	isNewOrder?: boolean
): Segment[] => {
	const passengerSeats = passenger.seats?.length ? passenger.seats : [];
	const selectedSeatsBySegment = getSelectedSeatsBySegment(passengerSeats);

	return doubleSeatsAdapter(
		seatMap.segments.map(segment => {
			const segmentSeats = selectedSeatsBySegment.get(segment.segmentsInfo.id) ?? [];
			const hasDoubleSeats = segmentSeats.filter(seat => !!seat.number).length > 1;

			return {
				isSeatsAvailableInCheckinOnly: segment.isSeatsAvailableInCheckinOnly,
				services: getSeatServicesForSegments(segment),
				sunSide: segment.sunSide,
				segmentInfo: segment.segmentsInfo,
				amenities: amenities,
				decks: segment.decks.map(deck => ({
					...deck,
					rows: deck.rows.map(row => ({
						...row,
						seats: row.seats?.map(
							getAvailabilitySeat(passenger, passengers, selectedSeats, isNewOrder, hasDoubleSeats)
						),
						parts: row.parts?.map(part => ({
							...part,
							seats: part.seats?.map(
								getAvailabilitySeat(passenger, passengers, selectedSeats, isNewOrder, hasDoubleSeats)
							)
						}))
					}))
				}))
			};
		})
	);
};

export const isSeatAvailableAndExistent = (seat: Seat) => {
	return seat.isAvailable && seat.isExistent && !seat.isAisle;
};

export const seatMapAdapter = (
	seatMap: FlightSeatMapFragment,
	additionalServices: OrderAdditionalServicesFragment
): SeatMapWithRowParts => splitSeatMapRows(addServicesToSeats(seatMap, additionalServices));

export const getOppositeDirection = (direction: CombinedDirection) =>
	direction === CombinedDirection.Left ? CombinedDirection.Right : CombinedDirection.Left;

export const getCombinedNumber = (seat: Seat, combinedWith?: CombinedWith) => {
	if (!combinedWith) {
		return null;
	}

	return Object.keys(combinedWith).filter(key => key !== seat.number)[0];
};

export const getSelectedSeatsBySegment = (passengerSeats: SelectedSeatsServiceType[] = []) => {
	const selectedSeatsBySegmentsMap = new Map<string, SelectedSeatsServiceType[]>();

	passengerSeats.forEach(seat =>
		selectedSeatsBySegmentsMap.set(seat.segmentId, [
			...(selectedSeatsBySegmentsMap.get(seat.segmentId) || []),
			seat
		])
	);

	return selectedSeatsBySegmentsMap;
};

export const getSeatsWithDoubleSeats = (passengerSeats: SelectedSeatsServiceType[] = []) => {
	const seatsWithDoubleSeatsMap = new Map<string, string[]>();

	const selectedSeatsBySegments = getSelectedSeatsBySegment(passengerSeats);

	selectedSeatsBySegments.forEach(seats => {
		const seatsWithNumber = seats.filter(seat => !!seat.number);

		if (seatsWithNumber.length > 1) {
			const seatNumbers = seatsWithNumber.map(seat => seat.number);

			seatsWithNumber.forEach(seat => {
				seatsWithDoubleSeatsMap.set(seat.number, seatNumbers);
			});
		}
	});

	return seatsWithDoubleSeatsMap;
};

export const buildSelectedSeatsBySegmentMap = (
	passengers: PassengerState[],
	segmentId: string
): Map<string, SelectedSeatsServiceType[]> => {
	const selectedSeatsBySegmentMap = new Map<string, SelectedSeatsServiceType[]>();

	passengers.forEach(passenger => {
		if (passenger.seats?.length) {
			const seatsBySegment = passenger.seats.filter(seat => seat.segmentId === segmentId);

			seatsBySegment.forEach(seat => {
				selectedSeatsBySegmentMap.set(seat.number, seatsBySegment);
			});
		}
	});

	return selectedSeatsBySegmentMap;
};

export const buildPassengerSegments = ({
	seatMaps,
	additionalServices,
	passengers,
	passenger,
	segmentIndex,
	amenities,
	isNewOrder
}: BuildPassengerSegmentsOptions): Segment[] => {
	return getSegments(
		seatMapAdapter(seatMaps, additionalServices),
		passenger,
		passengers,
		buildSelectedSeatsBySegmentMap(passengers, segmentIndex.toString()),
		amenities,
		isNewOrder
	);
};

export const buildPassengerFiltersFromSegment = (segment?: Segment): PassengerFiltersData => {
	if (!segment) {
		return EMPTY_PASSENGER_FILTERS_DATA;
	}

	return {
		allowedFilters: collectSeatMapRestrictionsFromSegment(segment),
		availableSeats: getAvailableSeatsFromSegment(segment)
	};
};

export const collectSeatMapRestrictionsFromSegment = (segment: Segment): SeatMapRestrictions[] => {
	const restrictions = new Set<SeatMapRestrictions>();

	segment.decks.forEach(deck =>
		deck.rows.forEach(row =>
			row.seats.forEach(seat => {
				if (seat.isAvailable && seat.restrictions) {
					seat.restrictions.forEach(restriction => restrictions.add(restriction));
				}
			})
		)
	);

	return Array.from(restrictions);
};

export const getAvailableSeatsFromSegment = (segment: Segment): Seat[] => {
	const availableSeats: Seat[] = [];

	segment.decks.forEach(deck =>
		deck.rows.forEach(row => {
			row.seats.forEach(seat => {
				if (seat.isAvailable) {
					availableSeats.push(seat as Seat);
				}
			});
		})
	);

	return availableSeats;
};

export const getAvailableSeatsCount = (seats: Seat[], activeFilters: SeatMapRestrictions[] = []) => {
	if (activeFilters.length === 0) {
		return seats.length;
	}

	const activeFiltersSet = new Set(activeFilters);

	return seats.reduce((count, seat) => {
		if (!seat.restrictions) {
			return count + 1;
		}

		return seat.restrictions.some(restriction => activeFiltersSet.has(restriction)) ? count : count + 1;
	}, 0);
};

export const getEffectivePassengerFilters = (
	seats: Seat[],
	allowedFilters: SeatMapRestrictions[] = [],
	activeFilters: SeatMapRestrictions[] = []
) => {
	const activeFilterSet = new Set(activeFilters);
	const baseCount = getAvailableSeatsCount(seats, activeFilters);

	const effectiveFilters = allowedFilters.map(allowedFilter => {
		const countWithFilter = activeFilterSet.has(allowedFilter)
			? baseCount
			: getAvailableSeatsCount(seats, [...activeFilters, allowedFilter]);

		const countWithoutFilter = activeFilterSet.has(allowedFilter)
			? getAvailableSeatsCount(
					seats,
					activeFilters.filter(activeFilter => activeFilter !== allowedFilter)
			  )
			: baseCount;

		const isEffective = countWithFilter !== countWithoutFilter && countWithFilter > 0;

		return {
			title: allowedFilter,
			isEffective
		};
	});

	return effectiveFilters.filter(filter => filter.isEffective || activeFilterSet.has(filter.title));
};
